import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { closed } from "../../components/drawer/drawerSlice";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createMealCategory,
  getAllMealCategories,
  getMealCategoryByID,
  updateMealCategory,
} from "./services/mealCategory.services";
import { clearMealCategoryDetails } from "./mealCategorySlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { MEALTYPE } from "../../_contstants/common";

const CreateMealCategory: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const MealCategoryDetail = useAppSelector(
    (state) => state.mealCategory.MealCategoryDetail
  );
  const MealCategoryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.mealCategory.formLoading);

  useEffect(() => {
    if (MealCategoryID) {
      dispatch(getMealCategoryByID(MealCategoryID));
    }
  }, [MealCategoryID]);

  useEffect(() => {
    return () => {
      dispatch(clearMealCategoryDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!MealCategoryID) {
        const payload = {
          Title: values.Title ? values.Title : "",
          Type: values.Type ? values.Type : null,
          IsActive: values.IsActive ? values.IsActive : true,
        };
        const response = await dispatch(createMealCategory(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealCategoryDetails());
          dispatch(getAllMealCategories());
        }
      } else {
        const payload = {
          ID: MealCategoryID,
          Title: values.Title ? values.Title : "",
          Type: values.Type ? values.Type : null,
          IsActive: values.IsActive ? values.IsActive : false,
        };
        const response = await dispatch(updateMealCategory(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealCategoryDetails());
          dispatch(getAllMealCategories());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={MealCategoryDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Title"
                label="Title"
                component={FormTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Type"
                label="Type"
                component={FormSelectionField}
                options={MEALTYPE?.map((mealtype: any) => {
                  return {
                    value: mealtype.value,
                    label: mealtype.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginRight: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
              <ButtonWithLoading
                label={MealCategoryID ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateMealCategory;
